import type {
  DiagnosticScopeItem,
  DiagnosticScopeItemsTotals,
  DiagnosticSnapshot,
} from "@/api/diagnostic";

import { num, rec } from "../diagnosticData";

type Validation = DiagnosticSnapshot["validation"];

/**
 * De onde o tile conta os ativos, decidido pelo backend (``scope_items_domain.py``):
 * a foto da aprovação ativa quando existe, senão a curadoria salva da última
 * execução concluída. A tela só repete o que o envelope diz.
 */
export type ScopeBasis = "approved" | "saved_curation";

const BASES: readonly ScopeBasis[] = ["approved", "saved_curation"];

/**
 * O que o tile "Scope items ativos" sabe dizer (EF-FUNC-004, #1344).
 *
 * - `counted`: o número existe, com a base que o produziu.
 * - `empty`: a execução existe e nenhum scope item tem veredito. Não é zero
 *   medido: é leitura sem resultado.
 * - `failed`: o domínio não fechou (`error`, `unauthorized`, `partial`). A
 *   execução pode existir, então mandar esperar outra análise seria afirmar o
 *   que não se sabe.
 * - `unknown`: não há execução, o bloco não chegou, ou o dado não diz a base.
 */
export type ScopeItemsTile =
  | {
      kind: "counted";
      basis: ScopeBasis;
      approvalVersion: number | null;
      executionId: string | undefined;
      active: number;
      lobCount: number | undefined;
      /** Ativos com observação visível no relatório; `undefined` se não vier contado. */
      withNote: number | undefined;
    }
  | { kind: "empty" }
  | { kind: "failed" }
  | { kind: "unknown" };

/**
 * Lê o envelope `scope_items` do bloco `validation`, e não uma seção: a
 * curadoria é lida ao vivo pelo backend, também com a Calibragem publicada.
 */
export function scopeItemsTile(validation: Validation | undefined): ScopeItemsTile {
  const envelope = validation?.scope_items;
  if (!envelope || envelope.state === "not_collected") return { kind: "unknown" };
  if (envelope.state === "empty") return { kind: "empty" };
  if (envelope.state !== "ok") return { kind: "failed" };

  const data = rec(envelope.data);
  const basis = BASES.find((known) => known === data.basis);
  const active = num(data.active);
  if (!basis || active == null) return { kind: "unknown" };

  return {
    kind: "counted",
    basis,
    approvalVersion: num(data.approval_version) ?? null,
    executionId: typeof data.execution_id === "string" ? data.execution_id : undefined,
    active,
    lobCount: num(data.lob_count),
    withNote: num(data.with_visible_note),
  };
}

// --------------------------------------------------------------------------
// linhas do detalhe
// --------------------------------------------------------------------------

/** Balde das linhas sem LoB, no filtro e na ordenação. Nunca vai para a tela. */
export const NO_LOB = "_NO_LOB";

/**
 * Uma linha do detalhe, nas colunas da EF. Não carrega de onde veio a decisão
 * nem quem a tomou: o detalhe vai para o cliente, e origem e autor ficam nas
 * telas internas (Catálogo e planilha). O endpoint (#1506) nem os manda.
 */
export interface ScopeItemDetailRow {
  scopeItemId: string;
  name: string | null;
  lob: string | null;
  /**
   * Da evidência de uso no ERP: o motor só a preenche no ramo `activate`. O
   * veredito do motor (Fit ERP) não vem para a linha: no Diagnóstico vale o
   * veredito da curadoria (21/10/2026).
   */
  confidence: string | null;
  cleanCore: boolean;
  /** Só a observação marcada para o relatório (RF-06); o endpoint anula a interna. */
  note: string | null;
}

/** O filtro de observação do protótipo: com e sem, só com, só sem. */
export type NoteFilter = "all" | "with" | "without";

export interface ScopeItemFilters {
  /** `undefined` para todas; `NO_LOB` para as sem LoB. */
  lob: string | undefined;
  /** Só olha a observação visível: a interna o endpoint já manda nula. */
  note: NoteFilter;
  search: string;
}

function byLobThenId(a: ScopeItemDetailRow, b: ScopeItemDetailRow): number {
  if (a.lob !== b.lob) {
    if (a.lob == null) return 1;
    if (b.lob == null) return -1;
    return a.lob.localeCompare(b.lob);
  }
  return a.scopeItemId.localeCompare(b.scopeItemId);
}

/** As linhas da tabela do detalhe, por LoB e código, sem LoB no fim. */
export function scopeItemDetailRows(
  items: readonly DiagnosticScopeItem[],
): ScopeItemDetailRow[] {
  return items
    .map((item) => ({
      scopeItemId: item.scope_item_id,
      name: item.scope_item_name ?? null,
      // LoB vazia é sem LoB: o "" é o valor da opção "todas" do filtro.
      lob: item.lob || null,
      confidence: item.confidence ?? null,
      cleanCore: item.clean_core === true,
      note: item.curation_note || null,
    }))
    .sort(byLobThenId);
}

/** As LoBs do filtro, ordenadas, com `NO_LOB` no fim quando alguma linha não tem. */
export function lobOptions(rows: readonly ScopeItemDetailRow[]): string[] {
  const lobs = Array.from(
    new Set(rows.flatMap((row) => (row.lob == null ? [] : [row.lob]))),
  ).sort((a, b) => a.localeCompare(b));
  return rows.some((row) => row.lob == null) ? [...lobs, NO_LOB] : lobs;
}

export function filterScopeItemRows(
  rows: readonly ScopeItemDetailRow[],
  filters: ScopeItemFilters,
): ScopeItemDetailRow[] {
  const needle = filters.search.trim().toLowerCase();
  return rows.filter((row) => {
    if (filters.lob === NO_LOB && row.lob != null) return false;
    if (filters.lob != null && filters.lob !== NO_LOB && row.lob !== filters.lob) return false;
    if (filters.note === "with" && row.note == null) return false;
    if (filters.note === "without" && row.note != null) return false;
    if (!needle) return true;
    return (
      row.scopeItemId.toLowerCase().includes(needle) ||
      (row.name ?? "").toLowerCase().includes(needle)
    );
  });
}

/** Um chip do indicador "Por linha de negócio". */
export interface LobChip {
  /** A LoB, ou `NO_LOB` para os ativos sem LoB. */
  lob: string;
  count: number;
}

/**
 * Os chips de LoB do detalhe, do maior para o menor. `by_lob` deixa de fora os
 * ativos sem LoB; eles entram num balde `NO_LOB` no fim, com os de LoB vazia,
 * para os chips somarem o número de ativos.
 */
export function lobChips(
  totals: Pick<DiagnosticScopeItemsTotals, "active" | "by_lob">,
): LobChip[] {
  const chips = Object.entries(totals.by_lob)
    .filter(([lob]) => lob !== "")
    .map(([lob, count]) => ({ lob, count }))
    .sort((a, b) => b.count - a.count || a.lob.localeCompare(b.lob));
  const withoutLob = totals.active - chips.reduce((sum, chip) => sum + chip.count, 0);
  return withoutLob > 0 ? [...chips, { lob: NO_LOB, count: withoutLob }] : chips;
}
